import type { Components } from '@/api/generated/api';
import {
  assignSubjectsToGroup,
  createSchoolSubjectGroup as apiCreateClassGroup,
  createTenantSchoolSubjectGroupTemplate as apiCreateTenantTemplate,
  deleteSchoolSubjectGroup as apiDeleteGroup,
  fetchSchoolSubjectGroupsForClass,
  fetchTenantSchoolSubjectGroupTemplates,
  moveSchoolSubjectTemplates as apiMoveTemplates,
  updateSchoolSubjectGroup as apiUpdateGroup,
} from '@/api/resources/schoolSubjectGroup.api';
import {
  assignProviderSubjectsToGroup,
  createProviderSchoolSubjectGroupTemplate as apiCreateProviderTemplate,
  fetchProviderSchoolSubjectGroupTemplates,
} from '@/api/resources/schoolSubjectGroup.provider.api';
import { useCurrentTemplateSet } from '@/composables/provider/useCurrentTemplateSet';
import { performanceAssessmentKeys } from '@/composables/performanceAssessment.keys';
import { useProviderSubjectTemplates } from '@/composables/provider/useSubjectTemplates';
import { usePermissions } from '@/composables/usePermissions';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import type { ISchoolSubjectMoveTarget } from '@/utils/schoolSubject.utils';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, type ComputedRef } from 'vue';
import { useSchoolSubject } from '../schoolSubject/useSchoolSubject';
import { schoolSubjectGroupKeys } from './schoolSubjectGroup.keys';

export enum ESchoolSubjectGroupMode {
  CLASS = 'class',
  TENANT = 'tenant',
  PROVIDER = 'provider',
  SELECT = 'select',
  ONBOARDING = 'onboarding',
}

export type TSchoolSubjectWithGroupId = Components.Schemas.SchoolSubjectDto & { groupId: number };

interface IMoveSchoolSubjectsParams extends ISchoolSubjectMoveTarget {
  subjectIds: number[];
  mode: ESchoolSubjectGroupMode;
  targetOrder?: number;
}

interface ISchoolSubjectGroupComposable {
  moveSchoolSubjects: (params: IMoveSchoolSubjectsParams) => Promise<boolean>;
  allSchoolSubjectGroups: ComputedRef<Components.Schemas.SchoolSubjectGroupDto[]>;
  allSchoolSubjectGroupTemplates: ComputedRef<Components.Schemas.SchoolSubjectGroupDto[]>;
  allProviderSchoolSubjectGroupTemplates: ComputedRef<Components.Schemas.SchoolSubjectGroupDto[]>;
  assignSchoolSubjectToGroup: (
    groupId: number,
    dto: Components.Schemas.AssignSubjectDto,
    mode: ESchoolSubjectGroupMode,
  ) => Promise<void>;
  moveSubjectToGroup: (subjectId: number, targetGroupId: number, mode: ESchoolSubjectGroupMode) => Promise<void>;
  createSchoolSubjectGroup: (
    classId: number,
    dto: Components.Schemas.CreateSchoolSubjectGroupDto,
  ) => Promise<Components.Schemas.SchoolSubjectGroupDto>;
  createSchoolSubjectGroupTemplate: (
    dto: Components.Schemas.CreateSchoolSubjectGroupDto,
  ) => Promise<Components.Schemas.SchoolSubjectGroupDto>;
  createProviderSchoolSubjectGroupTemplate: (
    dto: Components.Schemas.CreateSchoolSubjectGroupProviderDto,
  ) => Promise<Components.Schemas.SchoolSubjectGroupDto>;
  deleteSchoolSubjectGroup: (
    dto: Components.Schemas.SchoolSubjectGroupDto,
    mode: ESchoolSubjectGroupMode,
  ) => Promise<void>;
  getSchoolSubjectGroupsForClass: (classId: number) => Promise<Components.Schemas.SchoolSubjectGroupDto[]>;
  updateSchoolSubjectGroup: (
    id: number,
    dto: Components.Schemas.UpdateSchoolSubjectGroupDto,
    mode: ESchoolSubjectGroupMode,
  ) => Promise<void>;
  refreshSchoolSubjectGroupTemplates: () => Promise<void>;
  invalidateByMode: (mode: ESchoolSubjectGroupMode) => Promise<void>;
}

export const useSchoolSubjectGroup = (): ISchoolSubjectGroupComposable => {
  const { createSuccessToast, catchError, editSuccessToast, deleteSuccessToast } = useToast();
  const queryClient = useQueryClient();

  const { currentClass } = useCurrentClass();
  const { hasProviderAccess, hasTenantAdminAccess, canManageCompetences } = usePermissions();
  const { currentTemplateSet } = useCurrentTemplateSet();

  const { refresh: refreshProviderTemplates } = useProviderSubjectTemplates();
  const { refresh: refreshSubjectTemplates } = useSchoolSubject();

  const classId = computed(() => currentClass.value?.id);
  const templateSetId = computed(() => currentTemplateSet.value?.id);

  const classListKey = computed(() => schoolSubjectGroupKeys.classList(classId.value));
  const tenantTemplatesKey = computed(() => schoolSubjectGroupKeys.tenantTemplates(hasTenantAdminAccess.value));
  const providerTemplatesKey = computed(() =>
    schoolSubjectGroupKeys.providerTemplates(templateSetId.value, hasProviderAccess.value),
  );

  const getSchoolSubjectGroupsForClass = (id: number) => fetchSchoolSubjectGroupsForClass({ classId: id });

  const classQuery = useQueryWithToast({
    queryKey: classListKey,
    enabled: computed(() => Boolean(classId.value)),
    queryFn: async () => {
      if (!classId.value) return [];
      return fetchSchoolSubjectGroupsForClass({ classId: classId.value });
    },
  });

  const tenantTemplateQuery = useQueryWithToast({
    queryKey: tenantTemplatesKey,
    enabled: computed(() => Boolean(hasTenantAdminAccess.value || canManageCompetences.value)),
    queryFn: async () => {
      return fetchTenantSchoolSubjectGroupTemplates();
    },
  });

  const providerTemplateQuery = useQueryWithToast({
    queryKey: providerTemplatesKey,
    enabled: computed(() => Boolean(hasProviderAccess.value && templateSetId.value)),
    queryFn: async () => {
      if (!hasProviderAccess.value || !templateSetId.value) return [];
      return fetchProviderSchoolSubjectGroupTemplates({ templateSetId: templateSetId.value });
    },
  });

  const allSchoolSubjectGroups = computed(() => classQuery.data.value ?? []);
  const allSchoolSubjectGroupTemplates = computed(() => tenantTemplateQuery.data.value ?? []);
  const allProviderSchoolSubjectGroupTemplates = computed(() => providerTemplateQuery.data.value ?? []);

  const invalidateByMode = async (mode: ESchoolSubjectGroupMode): Promise<void> => {
    if (mode === ESchoolSubjectGroupMode.CLASS) {
      await queryClient.invalidateQueries({ queryKey: classListKey.value });
      return;
    }
    if (mode === ESchoolSubjectGroupMode.TENANT || mode === ESchoolSubjectGroupMode.ONBOARDING) {
      await queryClient.invalidateQueries({ queryKey: tenantTemplatesKey.value });
      return;
    }
    if (mode === ESchoolSubjectGroupMode.PROVIDER) {
      await queryClient.invalidateQueries({ queryKey: providerTemplatesKey.value });
    }
  };

  const refreshLinkedTemplates = async (mode: ESchoolSubjectGroupMode): Promise<void> => {
    if (mode === ESchoolSubjectGroupMode.PROVIDER) {
      await refreshProviderTemplates();
      return;
    }
    if (mode === ESchoolSubjectGroupMode.CLASS || mode === ESchoolSubjectGroupMode.TENANT) {
      await refreshSubjectTemplates();
    }
  };

  const createSchoolSubjectGroup = async (
    classIdParam: number,
    dto: Components.Schemas.CreateSchoolSubjectGroupDto,
  ): Promise<Components.Schemas.SchoolSubjectGroupDto> => {
    return catchError(async () => {
      const created = await apiCreateClassGroup({ classId: classIdParam, dto });
      await queryClient.invalidateQueries({ queryKey: classListKey.value });
      createSuccessToast();
      return created;
    });
  };

  const createSchoolSubjectGroupTemplate = async (
    dto: Components.Schemas.CreateSchoolSubjectGroupDto,
  ): Promise<Components.Schemas.SchoolSubjectGroupDto> => {
    return catchError(async () => {
      const created = await apiCreateTenantTemplate({ dto });
      await queryClient.invalidateQueries({ queryKey: tenantTemplatesKey.value });
      createSuccessToast();
      return created;
    });
  };

  const createProviderSchoolSubjectGroupTemplate = async (
    dto: Components.Schemas.CreateSchoolSubjectGroupProviderDto,
  ): Promise<Components.Schemas.SchoolSubjectGroupDto> => {
    return catchError(async () => {
      const created = await apiCreateProviderTemplate({ dto });
      await queryClient.invalidateQueries({ queryKey: providerTemplatesKey.value });
      createSuccessToast();
      return created;
    });
  };

  const refreshSchoolSubjectGroupTemplates = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: tenantTemplatesKey.value });
  };

  const deleteSchoolSubjectGroup = async (
    dto: Components.Schemas.SchoolSubjectGroupDto,
    mode: ESchoolSubjectGroupMode,
  ): Promise<void> => {
    await catchError(async () => {
      await apiDeleteGroup({ groupId: dto.id });
      await invalidateByMode(mode);
      await refreshLinkedTemplates(mode);
      deleteSuccessToast();
    });
  };

  const updateSchoolSubjectGroup = async (
    id: number,
    dto: Components.Schemas.UpdateSchoolSubjectGroupDto,
    mode: ESchoolSubjectGroupMode,
  ): Promise<void> => {
    await catchError(async () => {
      await apiUpdateGroup({ groupId: id, dto });
      await invalidateByMode(mode);
      await refreshLinkedTemplates(mode);
      editSuccessToast();
    });
  };

  const assignSubjectsToGroupRequest = async (
    groupId: number,
    dto: Components.Schemas.AssignSubjectDto,
    mode: ESchoolSubjectGroupMode,
  ): Promise<void> => {
    if (mode === ESchoolSubjectGroupMode.PROVIDER) {
      await assignProviderSubjectsToGroup({ groupId, dto });
    } else {
      await assignSubjectsToGroup({ groupId, dto });
    }
  };

  const assignSchoolSubjectToGroup = async (
    groupId: number,
    dto: Components.Schemas.AssignSubjectDto,
    mode: ESchoolSubjectGroupMode,
  ): Promise<void> => {
    await catchError(async () => {
      await assignSubjectsToGroupRequest(groupId, dto, mode);

      await invalidateByMode(mode);
      await refreshLinkedTemplates(mode);
      editSuccessToast();
    });
  };

  const moveSubjectToGroup = async (
    subjectId: number,
    targetGroupId: number,
    mode: ESchoolSubjectGroupMode,
  ): Promise<void> => {
    await assignSchoolSubjectToGroup(targetGroupId, { subjectId }, mode);
  };

  const moveSchoolSubjectTemplates = async (dto: Components.Schemas.MoveSchoolSubjectTemplatesDto): Promise<void> => {
    await catchError(async () => {
      await apiMoveTemplates(dto);
      await Promise.all([
        invalidateByMode(ESchoolSubjectGroupMode.TENANT),
        refreshSubjectTemplates(),
        queryClient.invalidateQueries({ queryKey: performanceAssessmentKeys.lists() }),
      ]);
      editSuccessToast();
    });
  };

  const moveSubjectsToGroup = async ({
    subjectIds,
    targetGroupId,
    mode,
    targetOrder,
  }: {
    subjectIds: number[];
    targetGroupId: number;
    mode: ESchoolSubjectGroupMode;
    targetOrder?: number;
  }): Promise<void> => {
    if (!subjectIds.length) return;

    await catchError(async () => {
      for (const [index, subjectId] of subjectIds.entries()) {
        await assignSubjectsToGroupRequest(
          targetGroupId,
          {
            subjectId,
            order: targetOrder == null ? undefined : targetOrder + index,
          },
          mode,
        );
      }
      await invalidateByMode(mode);
      await refreshLinkedTemplates(mode);
      editSuccessToast();
    });
  };

  const moveSchoolSubjects = async (params: IMoveSchoolSubjectsParams): Promise<boolean> => {
    const { subjectIds, targetSchoolLevel, targetGroupId, mode } = params;
    if (!subjectIds.length) return false;
    const [firstSubjectId, ...remainingSubjectIds] = subjectIds;

    if (mode === ESchoolSubjectGroupMode.TENANT && targetSchoolLevel !== undefined) {
      await moveSchoolSubjectTemplates({
        schoolSubjectIds: [firstSubjectId, ...remainingSubjectIds],
        targetSchoolLevel,
        targetGroupId,
      });
      return true;
    }
    if (targetGroupId === null) return false;
    await moveSubjectsToGroup({ ...params, targetGroupId });
    return true;
  };

  return {
    moveSchoolSubjects,
    allProviderSchoolSubjectGroupTemplates,
    allSchoolSubjectGroups,
    allSchoolSubjectGroupTemplates,
    assignSchoolSubjectToGroup,
    moveSubjectToGroup,
    createProviderSchoolSubjectGroupTemplate,
    createSchoolSubjectGroup,
    createSchoolSubjectGroupTemplate,
    deleteSchoolSubjectGroup,
    getSchoolSubjectGroupsForClass,
    refreshSchoolSubjectGroupTemplates,
    updateSchoolSubjectGroup,
    invalidateByMode,
  };
};
